import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Loader2,
  ShieldOff,
  ShieldCheck,
  Plus,
  Copy,
  Check,
  Trash2,
  RefreshCw,
} from "lucide-react";

const SUPERADMIN_EMAIL_FOR_FORCE = "sovic.srecko@gmail.com";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function CopyPill({ value, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* noop */
        }
      }}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:border-primary/40 text-muted-foreground hover:text-primary"
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function CreateUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/users", { email, role });
      setCreated(data);
      toast.success("Admin created");
      onCreated();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setEmail("");
          setRole("admin");
          setError("");
          setCreated(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="users-create-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Create admin
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="users-create-dialog">
        <DialogHeader>
          <DialogTitle>Create new admin user</DialogTitle>
        </DialogHeader>
        {created ? (
          <div className="space-y-4" data-testid="users-create-result">
            <p className="text-sm text-muted-foreground">
              Account created. The temporary password is shown <b>once</b> —
              copy it now.
            </p>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">Email:</span>{" "}
                <span data-testid="users-create-result-email">{created.user.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-muted-foreground">Temp password:</span>
                <code
                  className="bg-secondary px-2 py-1 rounded tracking-wider"
                  data-testid="users-create-result-password"
                >
                  {created.temp_password}
                </code>
                <CopyPill value={created.temp_password} testid="users-create-result-copy" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setOpen(false)} data-testid="users-create-result-close">
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-email">Email</Label>
              <Input
                id="new-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="users-create-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="users-create-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin" data-testid="users-create-role-admin">
                    Admin
                  </SelectItem>
                  <SelectItem value="superadmin" data-testid="users-create-role-superadmin">
                    Superadmin
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && (
              <p role="alert" aria-live="polite" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="users-create-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ForceDisable2FA({ targetUser, onDone, canForce }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!canForce) return null;

  const confirm = async () => {
    setSubmitting(true);
    try {
      await api.post(`/admin/users/${targetUser.id}/force-disable-2fa`);
      toast.success(`2FA disabled for ${targetUser.email}`);
      onDone();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-destructive/40 text-destructive hover:bg-destructive/10"
          data-testid={`force-disable-2fa-button-${targetUser.id}`}
        >
          <ShieldOff className="w-3.5 h-3.5 mr-1.5" /> Force disable 2FA
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Force disable 2FA?</AlertDialogTitle>
          <AlertDialogDescription>
            This will disable 2FA for <span className="text-foreground">{targetUser.email}</span>{" "}
            immediately and log the action. Continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`force-disable-2fa-cancel-${targetUser.id}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirm}
            disabled={submitting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid={`force-disable-2fa-confirm-${targetUser.id}`}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Force disable"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DeleteUserButton({ targetUser, onDone }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const confirm = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/admin/users/${targetUser.id}`);
      toast.success(`User ${targetUser.email} deleted`);
      onDone();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-border text-muted-foreground hover:text-destructive hover:border-destructive/40"
          data-testid={`delete-user-button-${targetUser.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete user?</AlertDialogTitle>
          <AlertDialogDescription>
            Permanently remove <span className="text-foreground">{targetUser.email}</span>?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirm}
            disabled={submitting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid={`delete-user-confirm-${targetUser.id}`}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function UsersTable({ users, currentUser, canForce, onChange }) {
  return (
    <div className="border border-border rounded-xl overflow-x-auto" data-testid="users-table-wrap">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>2FA</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody data-testid="users-table-body">
          {users.map((u) => (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
              <TableCell className="font-medium" data-testid={`user-email-${u.id}`}>
                {u.email}
              </TableCell>
              <TableCell>
                <span className="text-xs uppercase tracking-widest text-primary/90">{u.role}</span>
              </TableCell>
              <TableCell>
                {u.totp_enabled ? (
                  <span className="inline-flex items-center gap-1 text-primary text-xs">
                    <ShieldCheck className="w-3.5 h-3.5" /> Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
                    <ShieldOff className="w-3.5 h-3.5" /> Off
                  </span>
                )}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">{formatDate(u.created_at)}</TableCell>
              <TableCell className="text-right">
                <div className="inline-flex gap-2">
                  {u.totp_enabled && (
                    <ForceDisable2FA
                      targetUser={u}
                      onDone={onChange}
                      canForce={canForce}
                    />
                  )}
                  {u.id !== currentUser?.id &&
                    u.email !== SUPERADMIN_EMAIL_FOR_FORCE && (
                      <DeleteUserButton targetUser={u} onDone={onChange} />
                    )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function AuditTable({ items }) {
  return (
    <div className="border border-border rounded-xl overflow-x-auto" data-testid="audit-table-wrap">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Actor</TableHead>
            <TableHead>Target</TableHead>
            <TableHead>Meta</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody data-testid="audit-table-body">
          {items.map((it) => (
            <TableRow key={it._id} data-testid={`audit-row-${it._id}`}>
              <TableCell className="text-xs text-muted-foreground">{formatDate(it.created_at)}</TableCell>
              <TableCell>
                <span className="text-xs uppercase tracking-widest text-primary/90">{it.action}</span>
              </TableCell>
              <TableCell className="text-xs">{it.actor_email || "—"}</TableCell>
              <TableCell className="text-xs">{it.target_email || "—"}</TableCell>
              <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                {it.metadata && Object.keys(it.metadata).length
                  ? JSON.stringify(it.metadata)
                  : "—"}
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                No audit entries yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default function AdminUsers() {
  const { user } = useAuth();
  const canForce = user?.email?.toLowerCase() === SUPERADMIN_EMAIL_FOR_FORCE;

  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, a] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/audit-log"),
      ]);
      setUsers(u.data);
      setAudit(a.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-users-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80">Administration</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Users & audit</h1>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={load}
              className="border-border"
              data-testid="users-refresh-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <CreateUserDialog onCreated={load} />
          </div>
        </div>

        <Tabs defaultValue="users">
          <TabsList data-testid="users-tabs">
            <TabsTrigger value="users" data-testid="users-tab-trigger">Users</TabsTrigger>
            <TabsTrigger value="audit" data-testid="audit-tab-trigger">Audit log</TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="mt-6">
            {loading ? (
              <div className="py-12 flex justify-center text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : (
              <UsersTable users={users} currentUser={user} canForce={canForce} onChange={load} />
            )}
          </TabsContent>
          <TabsContent value="audit" className="mt-6">
            {loading ? (
              <div className="py-12 flex justify-center text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : (
              <AuditTable items={audit} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
